import { __awaiter } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, Fragment, } from 'react';
import useAsyncEffect from 'use-async-effect';
import SketchField from '@powerednow/react-sketch/src/components/SketchField';
import makeStyles from '@mui/styles/makeStyles';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import DrawIcon from '@mui/icons-material/Draw';
import Crop54Icon from '@mui/icons-material/Crop54';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import RttIcon from '@mui/icons-material/Rtt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import BrushIcon from '@mui/icons-material/Brush';
import PaletteIcon from '@mui/icons-material/Palette';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import PanToolOutlined from '@mui/icons-material/PanToolOutlined';
import { AppBar, Toolbar, TextField, Slider, IconButton, Menu, MenuItem, Tooltip, InputAdornment, Popover, } from '@mui/material';
import { useResponsive } from '../hooks/responsive';
import Optional from '../fields/Optional';
const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        height: '100%',
        width: '100%',
    },
    toolbar: {
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
    },
    formControl: {
        minWidth: 60,
        marginRight: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            marginRight: 0,
        },
    },
    slider: {
        width: 100,
        [theme.breakpoints.down('sm')]: {
            width: 70,
        },
    },
    textField: {
        marginRight: theme.spacing(2),
    },
    lineColor: {
        width: '80px',
        marginBottom: 0,
        paddingLeft: '5px',
        paddingBottom: 0,
    },
    fillColor: {
        width: '120px',
        marginBottom: 0,
        paddingLeft: '5px',
        paddingRight: 0,
        paddingBottom: 0,
        [theme.breakpoints.down('sm')]: {
            width: '100px',
            marginBottom: 0,
            paddingLeft: '5px',
            paddingRight: 0,
            paddingBottom: 0,
        },
    },
    icon: {
        marginRight: 0,
        paddingRight: 0,
        paddingLeft: 0,
    },
    iconButton: {
        paddingRight: 5,
        paddingLeft: 0,
    },
    iconButtonActive: {
        padding: '6px',
        border: '2px solid #3498DB',
        borderRadius: '10px',
    },
    smallIcon: {
        fontSize: 15,
    },
    mediumIcon: {
        fontSize: 22,
    },
    largeIcon: {
        fontSize: 36,
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    smallBrushIcon: {
        fontSize: 22,
        [theme.breakpoints.down('md')]: {
            fontSize: 15,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: 12,
        },
    },
    largeBrushIcon: {
        fontSize: 36,
        [theme.breakpoints.down('md')]: {
            fontSize: 28,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: 20,
        },
    },
}));
const OptionalTools = Optional(Fragment);
function CommonTools({ tool, toolIcons, handleToolChange, classes, }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [previousTool, setPreviousTool] = useState('pencil');
    useEffect(() => {
        if (Object.keys(toolIcons).includes(tool)) {
            setPreviousTool(tool);
        }
    }, [tool]);
    const handleOpenToolMenu = event => {
        if (tool !== previousTool) {
            handleToolChange(previousTool);
        }
        else {
            setAnchorEl(event.currentTarget);
        }
    };
    const handleCloseToolMenu = () => {
        setAnchorEl(null);
    };
    const onToolChange = toolKey => {
        handleToolChange(toolKey);
        handleCloseToolMenu();
    };
    return (_jsxs(_Fragment, { children: [_jsx(IconButton, { color: "secondary", onClick: handleOpenToolMenu, className: tool === previousTool ? classes.iconButtonActive : undefined, children: toolIcons[tool === previousTool ? tool : previousTool] }), _jsx(Menu, { anchorEl: anchorEl, open: Boolean(anchorEl), onClose: handleCloseToolMenu, children: Object.keys(toolIcons).map(key => (_jsx(MenuItem, { onClick: () => onToolChange(key), children: _jsx(IconButton, { color: key === tool ? 'secondary' : 'default', children: toolIcons[key] }) }, key))) })] }));
}
function ExtraTools({ hasSelection, orientation, onRemoveSelected, onDuplicate, onAddText, onToggleOrientation, }) {
    return (_jsxs(_Fragment, { children: [_jsx(IconButton, { color: "secondary", disabled: !hasSelection, onClick: onRemoveSelected, children: _jsx(DeleteForeverIcon, {}) }), _jsx(IconButton, { color: "secondary", disabled: !hasSelection, onClick: onDuplicate, children: _jsx(ContentCopyIcon, {}) }), _jsx(IconButton, { color: "secondary", onClick: onAddText, children: _jsx(RttIcon, {}) }), _jsx(IconButton, { color: "secondary", onClick: onToggleOrientation, children: orientation === 'landscape' ? _jsx(RotateLeftIcon, {}) : _jsx(RotateRightIcon, {}) })] }));
}
function SketchPopover({ anchorEl, onClose, children }) {
    return (_jsx(Popover, { open: Boolean(anchorEl), anchorEl: anchorEl, onClose: onClose, anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
        }, transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
        }, children: children }));
}
function ColorPickers({ lineColor, fillColor, handleLineColorChange, handleFillColorChange, classes, }) {
    const lineColorInputRef = React.useRef(null);
    const fillColorInputRef = React.useRef(null);
    const handleLineColorClick = () => {
        if (lineColorInputRef.current) {
            lineColorInputRef.current.click();
        }
    };
    const handleFillColorClick = () => {
        if (fillColorInputRef.current) {
            fillColorInputRef.current.click();
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: "Line Color", arrow: true, children: _jsx(TextField, { label: "", type: "color", value: lineColor, onChange: handleLineColorChange, className: classes.formControl, inputRef: lineColorInputRef, onClick: handleLineColorClick, InputProps: {
                        startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(BrushIcon, { className: classes.icon }) })),
                        classes: {
                            root: classes.lineColor,
                        },
                        inputProps: {
                            style: { paddingLeft: '0px', paddingRight: '5px' },
                        },
                    } }) }), _jsx(Tooltip, { title: "Fill Color", arrow: true, children: _jsx(TextField, { label: "", type: "color", value: fillColor || '#ffffff', onChange: handleFillColorChange, className: classes.formControl, onClick: handleFillColorClick, inputRef: fillColorInputRef, InputProps: {
                        startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(PaletteIcon, { className: classes.icon }) })),
                        classes: {
                            root: classes.fillColor,
                        },
                        inputProps: {
                            style: { paddingLeft: '0px', paddingRight: '0px' },
                        },
                        endAdornment: (_jsx(IconButton, { className: classes.iconButton, onClick: event => {
                                event.stopPropagation();
                                handleFillColorChange({ target: { value: 'transparent' } });
                            }, children: _jsx(HighlightOffIcon, { className: classes.icon }) })),
                    } }) })] }));
}
function MobileTools({ onRemoveSelected, onDuplicate, onAddText, onLineColorChange, onFillColorChange, lineColor, fillColor, hasSelection, orientation, onToggleOrientation, }) {
    const classes = useStyles();
    const [anchorElExtraTools, setAnchorElExtraTools] = useState(null);
    const [anchorElColorPicker, setAnchorElColorPicker] = useState(null);
    const handleOpenExtraToolsPopover = event => setAnchorElExtraTools(event.currentTarget);
    const handleCloseExtraToolsPopover = () => setAnchorElExtraTools(null);
    const handleOpenColorPickerPopover = event => setAnchorElColorPicker(event.currentTarget);
    const handleCloseColorPickerPopover = () => setAnchorElColorPicker(null);
    return (_jsxs(_Fragment, { children: [_jsx(IconButton, { onClick: handleOpenExtraToolsPopover, children: _jsx(MoreVertIcon, {}) }), _jsx(SketchPopover, { anchorEl: anchorElExtraTools, onClose: handleCloseExtraToolsPopover, children: _jsx(ExtraTools, { hasSelection: hasSelection, onRemoveSelected: onRemoveSelected, onDuplicate: onDuplicate, onAddText: onAddText, orientation: orientation, onToggleOrientation: onToggleOrientation }) }), _jsx(IconButton, { onClick: handleOpenColorPickerPopover, children: _jsx(PaletteIcon, {}) }), _jsx(SketchPopover, { anchorEl: anchorElColorPicker, onClose: handleCloseColorPickerPopover, children: _jsx(ColorPickers, { lineColor: lineColor, fillColor: fillColor, handleLineColorChange: onLineColorChange, handleFillColorChange: onFillColorChange, classes: classes }) })] }));
}
function DesktopTools({ hasSelection, onRemoveSelected, onDuplicate, onAddText, onLineColorChange, onFillColorChange, lineColor, fillColor, orientation, onToggleOrientation, }) {
    const classes = useStyles();
    return (_jsxs(_Fragment, { children: [_jsx(ExtraTools, { hasSelection: hasSelection, onRemoveSelected: onRemoveSelected, onDuplicate: onDuplicate, onAddText: onAddText, orientation: orientation, onToggleOrientation: onToggleOrientation }), _jsx(ColorPickers, { lineColor: lineColor, fillColor: fillColor, handleLineColorChange: onLineColorChange, handleFillColorChange: onFillColorChange, classes: classes })] }));
}
const Sketch = forwardRef(({ source, backgroundImage, height, width, hideToolbar, onBusy, }, ref) => {
    const classes = useStyles();
    const responsive = useResponsive();
    const [tool, setTool] = useState('pencil');
    const [lastNonZoomTool, setLastNonZoomTool] = useState('pencil');
    const [lineColor, setLineColor] = useState('#000000');
    const [fillColor, setFillColor] = useState();
    const [lineWidth, setLineWidth] = useState(3);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [hasSelection, setHasSelection] = useState(false);
    const [zoomFactor, setZoomFactor] = useState(1);
    const [orientation, setOrientation] = useState((width || 0) > (height || 0) ? 'portrait' : 'landscape');
    const sketchFieldRef = React.useRef(null);
    const handleToolChange = newTool => {
        if (!['zoom_in', 'zoom_out'].includes(tool)) {
            setLastNonZoomTool(tool);
        }
        setTool(newTool);
    };
    const changeSelectedObject = (property, value) => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.changeSeletedObject(property, value);
    };
    const handleLineColorChange = event => {
        setLineColor(event.target.value);
        if (hasSelection) {
            changeSelectedObject('stroke', lineColor);
        }
    };
    const handleFillColorChange = event => {
        const value = event.target.value === 'transparent' ? undefined : event.target.value;
        setFillColor(value);
        if (hasSelection) {
            changeSelectedObject('fill', value);
        }
    };
    const handleLineWidthChange = (event, newValue) => {
        setLineWidth(newValue);
        if (hasSelection) {
            changeSelectedObject('strokeWidth', newValue);
        }
    };
    const handleAddText = () => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.addText('New text', { fill: lineColor });
        handleToolChange('select');
    };
    const handleUndo = () => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.undo();
    };
    const handleRedo = () => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.redo();
    };
    const handleRemoveSelected = () => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.removeSelected();
    };
    const handleZoomIn = () => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.zoom(1.25);
    };
    const handleZoomOut = () => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.zoom(0.8);
    };
    const setZoomInTool = () => {
        if (tool !== 'zoom_in') {
            handleToolChange('zoom_in');
        }
        else {
            handleToolChange(lastNonZoomTool);
        }
    };
    const setZoomOutTool = () => {
        if (tool !== 'zoom_out') {
            handleToolChange('zoom_out');
        }
        else {
            handleToolChange(lastNonZoomTool);
        }
    };
    const handleRotationChange = () => {
        var _a;
        (_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.rotate90(orientation === 'portrait' ? 1 : -1);
        setOrientation(orientation === 'portrait' ? 'landscape' : 'portrait');
    };
    const handleDuplicate = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        yield ((_a = sketchFieldRef.current) === null || _a === void 0 ? void 0 : _a.copy());
        yield ((_b = sketchFieldRef.current) === null || _b === void 0 ? void 0 : _b.paste());
    });
    const handleComponentStateChange = () => {
        if (sketchFieldRef.current) {
            setCanUndo(sketchFieldRef.current.history.canUndo());
            setCanRedo(sketchFieldRef.current.history.canRedo());
            setHasSelection(sketchFieldRef.current.hasSelection);
            setZoomFactor(parseFloat(sketchFieldRef.current.zoomFactor.toFixed(2)));
        }
    };
    const handleKeyPress = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d;
        if (document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement) {
            return;
        }
        event.preventDefault();
        const code = event.which || event.keyCode;
        const charCode = String.fromCharCode(code).toLowerCase();
        if ((event.ctrlKey || event.metaKey) && charCode === 'd') {
            yield handleDuplicate();
        }
        else if ((event.ctrlKey || event.metaKey) && charCode === 'c') {
            (_c = sketchFieldRef.current) === null || _c === void 0 ? void 0 : _c.copy();
        }
        else if ((event.ctrlKey || event.metaKey) && charCode === 'v') {
            (_d = sketchFieldRef.current) === null || _d === void 0 ? void 0 : _d.paste();
        }
        else if ((event.ctrlKey || event.metaKey) && charCode === 'z') {
            if (event.shiftKey) {
                handleRedo();
            }
            else {
                handleUndo();
            }
        }
    }), []);
    useEffect(() => {
        if (hideToolbar) {
            handleToolChange('pan');
        }
    }, [hideToolbar]);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);
    useAsyncEffect(() => __awaiter(void 0, void 0, void 0, function* () {
        var _e, _f;
        if (onBusy)
            onBusy(true);
        if (source) {
            yield ((_e = sketchFieldRef.current) === null || _e === void 0 ? void 0 : _e.loadSvg(source));
        }
        if (backgroundImage) {
            yield ((_f = sketchFieldRef.current) === null || _f === void 0 ? void 0 : _f.setBackgroundFromDataUrl(backgroundImage, { resizeCanvas: true }));
        }
        if (onBusy)
            onBusy(false);
    }), [source, backgroundImage]);
    const getDataURL = () => __awaiter(void 0, void 0, void 0, function* () { var _g; return ((_g = sketchFieldRef.current) === null || _g === void 0 ? void 0 : _g.getDataURL()) || ''; });
    useImperativeHandle(ref, () => ({
        getDataURL() {
            return getDataURL();
        },
    }));
    const displayMode = responsive({
        lg: 'desktop',
        md: 'desktop',
        sm: 'mobile',
        xs: 'mobile',
    }, 'desktop');
    const toolIcons = Object.assign(Object.assign({}, (displayMode === 'mobile' ? { pan: _jsx(PanToolOutlined, {}) } : {})), { select: _jsx(HighlightAltIcon, {}), pencil: _jsx(DrawIcon, {}), line: _jsx(HorizontalRuleIcon, {}), rectangle: _jsx(Crop54Icon, {}), circle: _jsx(PanoramaFishEyeIcon, {}), arrow: _jsx(ArrowOutwardIcon, {}), highlighter: _jsx(FormatPaintIcon, {}) });
    const ResponsiveTools = responsive({
        lg: DesktopTools,
        md: DesktopTools,
        sm: MobileTools,
        xs: MobileTools,
    }, DesktopTools);
    return (_jsxs("div", { className: classes.root, children: [_jsx(AppBar, { position: "static", children: _jsxs(Toolbar, { className: classes.toolbar, children: [_jsx(IconButton, { color: "secondary", className: tool === 'zoom_in' ? classes.iconButtonActive : undefined, onClick: displayMode === 'desktop' ? handleZoomIn : setZoomInTool, disabled: zoomFactor > 10, children: _jsx(ZoomInIcon, {}) }), _jsx(IconButton, { color: "secondary", className: tool === 'zoom_out' ? classes.iconButtonActive : undefined, onClick: displayMode === 'desktop' ? handleZoomOut : setZoomOutTool, disabled: displayMode === 'desktop' && zoomFactor < 0.1, children: _jsx(ZoomOutIcon, {}) }), _jsxs(OptionalTools, { renderIf: !hideToolbar, children: [_jsx(IconButton, { color: "secondary", onClick: handleUndo, disabled: !canUndo, children: _jsx(UndoIcon, {}) }), _jsx(IconButton, { color: "secondary", onClick: handleRedo, disabled: !canRedo, children: _jsx(RedoIcon, {}) }), _jsx(CommonTools, { tool: tool, toolIcons: toolIcons, handleToolChange: handleToolChange, classes: classes }), _jsx(ResponsiveTools, { renderIf: !hideToolbar, lineColor: lineColor, fillColor: fillColor, onLineColorChange: handleLineColorChange, onFillColorChange: handleFillColorChange, onRemoveSelected: handleRemoveSelected, onDuplicate: handleDuplicate, onAddText: handleAddText, hasSelection: hasSelection, orientation: orientation, onToggleOrientation: handleRotationChange }), _jsxs("div", { className: classes.flexContainer, children: [_jsx(InputAdornment, { position: "start", children: _jsx(BrushIcon, { className: classes.smallBrushIcon }) }), _jsx(Slider, { size: responsive({ xs: 'small', sm: 'medium' }), value: lineWidth, onChange: handleLineWidthChange, "aria-labelledby": "line-width-slider", min: 1, max: 10, className: classes.slider }), _jsx(InputAdornment, { position: "end", children: _jsx(BrushIcon, { className: classes.largeBrushIcon }) })] })] })] }) }), _jsx(SketchField, { ref: sketchFieldRef, tool: tool, lineColor: lineColor, fillColor: fillColor, lineWidth: lineWidth, height: height, width: width, onChange: () => handleComponentStateChange() })] }));
});
export default Sketch;
